<script>
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

export default {
  name: 'AttachmentUpload',
  components: {
    FluentIcon,
  },
  props: {
    attachments: {
      type: Array,
      default: () => [],
    },
    maxFiles: {
      type: Number,
      default: 5,
    },
    maxFileSize: {
      type: Number,
      default: 40, // MB
    },
    allowedFileTypes: {
      type: Array,
      default: () => ['image/*', 'application/pdf', 'text/plain'],
    },
  },
  emits: ['attachmentAdded', 'attachmentRemoved', 'attachmentError'],
  data() {
    return {
      isDragging: false,
    };
  },
  computed: {
    canAddMoreFiles() {
      return this.attachments.length < this.maxFiles;
    },
    acceptString() {
      return this.allowedFileTypes.join(',');
    },
    maxFileSizeBytes() {
      return this.maxFileSize * 1024 * 1024; // Convert MB to bytes
    },
  },
  methods: {
    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.processFiles(files);
      // Reset input value to allow selecting the same file again
      event.target.value = '';
    },
    handleDrop(event) {
      event.preventDefault();
      this.isDragging = false;

      const files = Array.from(event.dataTransfer.files);
      this.processFiles(files);
    },
    handleDragOver(event) {
      event.preventDefault();
      this.isDragging = true;
    },
    handleDragLeave(event) {
      event.preventDefault();
      this.isDragging = false;
    },
    processFiles(files) {
      if (!this.canAddMoreFiles) {
        this.$emit('attachmentError', {
          message: this.$t('CANNED_MGMT.ADD.FORM.ATTACHMENTS.MAX_FILES_ERROR', {
            max: this.maxFiles,
          }),
        });
        return;
      }

      files.forEach(file => {
        if (!this.validateFile(file)) {
          return;
        }

        this.$emit('attachmentAdded', [file]);
      });
    },
    validateFile(file) {
      // Check file size
      if (!checkFileSizeLimit(file, this.maxFileSizeBytes)) {
        this.$emit('attachmentError', {
          message: this.$t('CANNED_MGMT.ADD.FORM.ATTACHMENTS.FILE_SIZE_ERROR', {
            maxSize: this.maxFileSize,
          }),
        });
        return false;
      }

      // Check file type
      const isValidType = this.allowedFileTypes.some(type => {
        if (type.endsWith('/*')) {
          const baseType = type.replace('/*', '');
          return file.type.startsWith(baseType);
        }
        return file.type === type;
      });

      if (!isValidType) {
        this.$emit('attachmentError', {
          message: this.$t('CANNED_MGMT.ADD.FORM.ATTACHMENTS.FILE_TYPE_ERROR'),
        });
        return false;
      }

      return true;
    },
    removeAttachment(index) {
      this.$emit('attachmentRemoved', index);
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / k ** i).toFixed(2)) + ' ' + sizes[i];
    },
    getFileIcon(fileType) {
      if (fileType.startsWith('image/')) return 'image';
      if (fileType.startsWith('video/')) return 'video';
      if (fileType.startsWith('audio/')) return 'audio';
      return 'document';
    },
  },
};
</script>

<template>
  <div class="attachment-upload">
    <!-- Upload Area -->
    <div
      v-if="canAddMoreFiles"
      class="upload-area"
      :class="{ 'is-dragging': isDragging }"
      @drop="handleDrop"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
    >
      <input
        ref="fileInput"
        type="file"
        :accept="acceptString"
        multiple
        class="hidden"
        @change="handleFileSelect"
      />

      <div class="upload-content">
        <FluentIcon icon="attach" class="upload-icon" />
        <p class="upload-text">
          {{ $t('CANNED_MGMT.ADD.FORM.ATTACHMENTS.DRAG_DROP_TEXT') }}
        </p>
        <button
          type="button"
          class="upload-button"
          @click="$refs.fileInput.click()"
        >
          {{ $t('CANNED_MGMT.ADD.FORM.ATTACHMENTS.SELECT_FILES') }}
        </button>
        <p class="upload-hint">
          {{ $t('CANNED_MGMT.ADD.FORM.ATTACHMENTS.HINT', {
            maxFiles: maxFiles,
            maxSize: maxFileSize,
          }) }}
        </p>
      </div>
    </div>

    <!-- Attachments List -->
    <div v-if="attachments.length > 0" class="attachments-list">
      <div
        v-for="(attachment, index) in attachments"
        :key="index"
        class="attachment-item"
      >
        <div class="attachment-preview">
          <FluentIcon
            :icon="getFileIcon(attachment.type)"
            class="attachment-icon"
          />
          <div class="attachment-info">
            <p class="attachment-name">{{ attachment.name }}</p>
            <p class="attachment-size">{{ formatFileSize(attachment.size) }}</p>
          </div>
        </div>
        <button
          type="button"
          class="remove-button"
          @click="removeAttachment(index)"
        >
          <FluentIcon icon="dismiss" />
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.attachment-upload {
  @apply w-full;
}

.upload-area {
  @apply border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-6 text-center transition-colors duration-200;

  &.is-dragging {
    @apply border-woot-500 bg-woot-50 dark:bg-woot-900/20;
  }

  &:hover {
    @apply border-slate-400 dark:border-slate-500;
  }
}

.upload-content {
  @apply flex flex-col items-center gap-3;
}

.upload-icon {
  @apply w-8 h-8 text-slate-400 dark:text-slate-500;
}

.upload-text {
  @apply text-sm text-slate-600 dark:text-slate-400;
}

.upload-button {
  @apply px-4 py-2 bg-woot-500 text-white rounded-md text-sm font-medium hover:bg-woot-600 transition-colors duration-200;
}

.upload-hint {
  @apply text-xs text-slate-500 dark:text-slate-400;
}

.attachments-list {
  @apply mt-4 space-y-2;
}

.attachment-item {
  @apply flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-lg;
}

.attachment-preview {
  @apply flex items-center gap-3 flex-1;
}

.attachment-icon {
  @apply w-6 h-6 text-slate-500 dark:text-slate-400;
}

.attachment-info {
  @apply flex-1 min-w-0;
}

.attachment-name {
  @apply text-sm font-medium text-slate-900 dark:text-slate-100 truncate;
}

.attachment-size {
  @apply text-xs text-slate-500 dark:text-slate-400;
}

.remove-button {
  @apply p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors duration-200;
}
</style> 